import {
  json,
  type ActionFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { Form, Link, useActionData } from "@remix-run/react";
import { clsx } from "clsx";
import * as React from "react";
import { z } from "zod";

import { Border } from "~/components/border";
import { Button } from "~/components/button";
import { Container } from "~/components/container";
import { FadeIn } from "~/components/fade-in";
import { List, ListItem } from "~/components/list";
import { Offices } from "~/components/offices";
import { PageIntro } from "~/components/page-intro";
import { SocialMedia } from "~/components/social-media";
import { env } from "~/env.server";
import { delivery, processSteps } from "~/lib/site";

export async function action({ request }: ActionFunctionArgs) {
  if (request.method.toUpperCase() !== "POST") {
    throw new Response(null, {
      status: 405,
      statusText: "Method Not Allowed",
      headers: { Allow: "POST" },
    });
  }

  let schema = z.object({
    first_name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email"),
    company: z.string().min(1, "Company is required"),
    // Optional on purpose: asking for a phone number up front costs us leads.
    phone: z.string().optional(),
    project: z.string().optional(),
    message: z.string().min(1, "Tell us what you're trying to ship"),
    budget: z.string().min(1, "Budget is required"),
  });

  let formData = Object.fromEntries(await request.formData());
  let result = schema.safeParse(formData);
  if (!result.success) {
    return json(
      { ok: false, errors: result.error.formErrors.fieldErrors },
      { status: 422 },
    );
  }

  let { first_name, last_name, email, ...notes } = result.data;

  let response = await fetch(
    `https://api.listomo.com/v1/accounts/${env.LISTOMO_ACCOUNT_ID}/lists/${env.LISTOMO_LEADS_LIST_ID}/contacts`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${env.LISTOMO_API_KEY}`,
      },
      body: JSON.stringify({
        contact: {
          email,
          first_name,
          last_name,
          form_ids: [env.LISTOMO_FORM_ID],
          notes: JSON.stringify(notes),
        },
      }),
    },
  );

  let data = await response.json();

  if (data.error) {
    console.error(data.error);
    return json({ ok: false, error: "Failed to submit" }, { status: 422 });
  }

  return json({ ok: true, errors: null });
}

export const meta: MetaFunction = () => {
  return [
    { title: "Start a project" },
    {
      name: "description",
      content: `Tell us what you’re trying to ship — platforms, deadline, and what’s in the way. You’ll hear back from an engineer within one business day.`,
    },
  ];
};

export default function Contact() {
  let actionData = useActionData<typeof action>();

  return (
    <>
      <PageIntro
        eyebrow="Start a project"
        title="Tell us what you’re trying to ship"
      >
        <p>
          Platforms, deadline, and what’s in the way. You’ll hear back within{" "}
          {delivery.responseTime} from the engineers who would do the work — not
          a sales team.
        </p>
      </PageIntro>

      <Container className="mt-24 sm:mt-32 lg:mt-40">
        <div className="grid grid-cols-1 gap-x-8 gap-y-24 lg:grid-cols-2">
          {actionData && actionData.ok ? (
            <FadeIn className="lg:order-last">
              <h2 className="font-display text-2xl font-semibold text-ink-50">
                Got it — talk soon.
              </h2>
              <p className="mt-4 text-base text-ink-300">
                We read every inquiry ourselves. Expect a reply within{" "}
                {delivery.responseTime} with our first read on the project and a
                link to book a {delivery.callLength} call.
              </p>
            </FadeIn>
          ) : (
            <ContactForm />
          )}
          <ContactDetails />
        </div>
      </Container>
    </>
  );
}

function TextInput({
  label,
  error,
  ...props
}: React.ComponentPropsWithoutRef<"input"> & {
  label: string;
  error?: string | undefined;
}) {
  let id = React.useId();

  return (
    <div className="group relative z-0 transition-all focus-within:z-10">
      <input
        type="text"
        id={id}
        {...props}
        placeholder=" "
        className={clsx(
          "peer block w-full border bg-transparent px-6 pb-4 pt-12 text-base/6 text-ink-50 transition focus:outline-none group-first:rounded-t-2xl group-last:rounded-b-2xl",
          error
            ? "border-red-500/60 focus:border-red-400"
            : "border-ink-700 focus:border-accent-400",
        )}
      />
      <label
        htmlFor={id}
        className="pointer-events-none absolute left-6 top-1/2 -mt-3 origin-left text-base/6 text-ink-400 transition-all duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-ink-50 peer-[:not(:placeholder-shown)]:-translate-y-4 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-ink-50"
      >
        {label}
      </label>
    </div>
  );
}

function RadioInput({
  label,
  ...props
}: React.ComponentPropsWithoutRef<"input"> & {
  label: string;
}) {
  return (
    <label className="flex gap-x-3">
      <input
        type="radio"
        {...props}
        className="h-6 w-6 flex-none appearance-none rounded-full border border-ink-600 outline-none transition checked:border-[0.5rem] checked:border-accent-400"
      />
      <span className="text-base/6 text-ink-100">{label}</span>
    </label>
  );
}

function ContactForm() {
  let actionData = useActionData<typeof action>();

  return (
    <FadeIn className="lg:order-last">
      <Form method="post">
        <h2 className="label text-accent-400">Project inquiry</h2>
        <div className="isolate mt-6 -space-y-px rounded-2xl">
          <TextInput
            label="First Name"
            name="first_name"
            autoComplete="given_name"
            error={
              actionData && "errors" in actionData
                ? actionData.errors?.first_name?.at(0)
                : undefined
            }
          />
          <TextInput
            label="Last Name"
            name="last_name"
            autoComplete="family_name"
            error={
              actionData && "errors" in actionData
                ? actionData.errors?.last_name?.at(0)
                : undefined
            }
          />
          <TextInput
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            error={
              actionData && "errors" in actionData
                ? actionData.errors?.email?.at(0)
                : undefined
            }
          />
          <TextInput
            label="Company"
            name="company"
            autoComplete="organization"
            error={
              actionData && "errors" in actionData
                ? actionData.errors?.company?.at(0)
                : undefined
            }
          />
          <TextInput
            label="Phone (optional)"
            type="tel"
            name="phone"
            autoComplete="tel"
            error={
              actionData && "errors" in actionData
                ? actionData.errors?.phone?.at(0)
                : undefined
            }
          />
          <TextInput
            label="What are you shipping?"
            name="message"
            error={
              actionData && "errors" in actionData
                ? actionData.errors?.message?.at(0)
                : undefined
            }
          />
          <div className="border border-ink-700 px-6 py-8 first:rounded-t-2xl last:rounded-b-2xl">
            <fieldset>
              <legend className="text-base/6 text-ink-400">
                What kind of help?
              </legend>
              <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2">
                {[
                  "Ship a new app",
                  "Add Kotlin Multiplatform",
                  "Rescue a stalled release",
                  "Embed senior engineers",
                ].map((option) => (
                  <RadioInput
                    key={option}
                    label={option}
                    name="project"
                    value={option}
                  />
                ))}
              </div>
            </fieldset>
          </div>
          <div className="border border-ink-700 px-6 py-8 first:rounded-t-2xl last:rounded-b-2xl">
            <fieldset>
              <legend className="text-base/6 text-ink-400">Budget</legend>
              <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2">
                <RadioInput
                  label="$25K – $50K"
                  name="budget"
                  value="$25K – $50K"
                />
                <RadioInput
                  label="$50K – $100K"
                  name="budget"
                  value="$50K – $100K"
                />
                <RadioInput
                  label="$100K – $150K"
                  name="budget"
                  value="$100K – $150K"
                />
                <RadioInput
                  label="More than $150K"
                  name="budget"
                  value="More than $150K"
                />
              </div>
              {actionData &&
              "errors" in actionData &&
              actionData.errors?.budget ? (
                <p className="mt-6 text-xs text-red-400">
                  {actionData.errors?.budget?.at(0)}
                </p>
              ) : null}
            </fieldset>
          </div>
        </div>
        <Button type="submit" size="lg" className="mt-10">
          Send it over
        </Button>
        <p className="mt-4 text-sm text-ink-400">
          Reply within {delivery.responseTime}. No newsletter signup, no drip
          sequence.
        </p>
      </Form>
    </FadeIn>
  );
}

function ContactDetails() {
  return (
    <FadeIn>
      <h2 className="label text-accent-400">What happens next</h2>
      <List className="mt-8">
        {processSteps.slice(0, 3).map((step) => (
          <ListItem key={step.title} title={`${step.when} — ${step.title}`}>
            {step.description}
          </ListItem>
        ))}
      </List>

      <Border className="mt-16 pt-16">
        <h2 className="label text-accent-400">Our office</h2>
        <Offices className="mt-5" />
      </Border>

      <Border className="mt-16 pt-16">
        <h2 className="label text-accent-400">Email us</h2>
        <dl className="mt-5 grid grid-cols-1 gap-8 text-sm sm:grid-cols-2">
          {[["New projects and questions", delivery.email]].map(
            ([label, email]) => (
              <div key={email}>
                <dt className="font-semibold text-ink-50">{label}</dt>
                <dd>
                  <Link
                    to={`mailto:${email}`}
                    className="text-ink-300 transition hover:text-ink-50"
                  >
                    {email}
                  </Link>
                </dd>
              </div>
            ),
          )}
        </dl>
      </Border>

      <Border className="mt-16 pt-16">
        <h2 className="label text-accent-400">Follow us</h2>
        <SocialMedia className="mt-6" />
      </Border>
    </FadeIn>
  );
}
